/**
 * **The local path, in one place — because D189 gave it a second caller.**
 *
 * This is `GateDetailScreen`'s `runLocalCommand` with the `setCommand` calls lifted out. It moved
 * here for one reason: D189(e) makes the Home tile an actuator too, and D25's receipt rule is not
 * a thing to have two copies of. *"A failed exchange is its own receipt — except when nothing was
 * sent at all, which is not an event about the gate"* is a sentence that has to be true on every
 * screen that can open a gate, and a rule stated twice is a rule that is about to be stated
 * differently.
 *
 * Five rules, in the order they execute:
 *
 *  1. **Only after a known result** (PEC-11). A receipt written before the controller answered
 *     would assert an opening nobody witnessed.
 *  2. **Queue a receipt, never a command.** The row carries what happened; there is no field on it
 *     that could carry an instruction, and the only thing that ever sends it is
 *     `POST /gates/{id}/receipts`.
 *  3. **A failed exchange is its own receipt** (D25: both, distinctly) — except `unavailable`,
 *     where the radio or the chooser never let a byte leave the phone. There is no physical event
 *     to record, and a receipt for it would put a row in Activity claiming an attempt on a gate
 *     that was never addressed.
 *  4. **An authentication failure re-fetches once and never guesses** (D84/PEC-25).
 *     `withCredential`'s `force` re-reads the *same* credential from the server; it does not try a
 *     different password, because a client that cycled candidates would be indistinguishable from
 *     an attacker probing the controller.
 *  5. **A receipt written with reception is uploaded now** — P16's step 8 had three triggers and
 *     all of them presumed a phone that had been offline, so a BLE open on a phone that never lost
 *     reception waited for the next cold launch. See the block that runs it for why it is never
 *     awaited and why an offline open deliberately drains nothing.
 *
 * **It reports the outcome and shows nothing.** PEC-11's *"show local success immediately"* is
 * still the caller's job and still runs before the upload — the two screens draw a chip and a
 * beaming tile respectively, and folding either in here would make this module own a rendering
 * decision it cannot see. **It also does not refresh the device cache: the screens own that
 * handle.** Rule 5 is not that handle — draining the outbox uploads a row this module just wrote,
 * through P16's own `reconcile.ts`, and reads and writes no P6 state.
 */

import { NetworkError } from '../auth/transport'
import type { KeyValueStore } from '../device-cache/store'
import type { ApiRequestFn } from '../screens/shared/api'
import type { CloudVerdict } from './autoPick'
import { BleAccessRefused, withCredential } from './credentials'
import { newReconciliationId, queueReceipt } from './outbox'
import { reconcileReceipts } from './reconcile'
import { bleAdapter, type BleCommand, type BleFailureReason, type BleOutcomeReason } from './webBluetooth'

/**
 * Why a local open did not happen.
 *
 * **`'no-credential'` is deliberately *not* a {@link BleFailureReason}.** That union is the closed
 * vocabulary the receipt schema declares, and widening it would put a new word in front of the
 * server for a case that produces no receipt at all — nothing was sent, so there is no event about
 * the gate to record (D25). It is a screen-local reason for a screen-local sentence.
 *
 * **`'refused'` joins it on the same terms** (R4/D216, via {@link BleOutcomeReason}): the
 * controller answered `CMD:Err`, so the gate *was* addressed — but see the receipt rule in
 * {@link openLocally} for why that still writes nothing.
 */
export type LocalFailureReason = BleOutcomeReason | 'no-credential'

export type LocalOpenResult =
  | { readonly ok: true; readonly latencyMs: number }
  | { readonly ok: false; readonly reason: LocalFailureReason; readonly latencyMs?: number }

export interface LocalOpenInput {
  readonly request: ApiRequestFn
  readonly gateId: string
  readonly command: BleCommand
  /**
   * Whether the cloud is reachable. Two distinct jobs: it suppresses D61's expiry check on a
   * **held** credential (D189(d)), and it gates PEC-25's single re-fetch, which cannot run without
   * a server to re-ask.
   *
   * **It does not decide whether a credential may be fetched, and the sentence that said so was
   * wrong.** `withCredential`'s offline branch is an early return for a copy already in hand; with
   * nothing held it falls through and **still fetches**, which that function states on its own
   * option (*"It suppresses the expiry check, not the fetch"*) and which D264 confirmed by
   * observation — an offline tap on a gate holding no credential reached the network and opened.
   * That matters beyond the wording: such a tap is a network call, so it can clear the session's
   * `online` latch, and any account of what a latched client does has to include it.
   */
  readonly online: boolean
  /** Absent on a session that has not resolved a user; the exchange still runs, the receipt does not. */
  readonly userId: string | undefined
  readonly stage: string
  readonly openStore: () => Promise<KeyValueStore>
}

export async function openLocally(input: LocalOpenInput): Promise<LocalOpenResult> {
  const attempt = async (force: boolean): Promise<LocalOpenResult> =>
    withCredential(
      input.request,
      input.gateId,
      (credential) => bleAdapter().open(credential, input.command),
      force ? { force: true, offline: !input.online } : { offline: !input.online },
    )

  let result: LocalOpenResult
  try {
    result = await attempt(false)
    // Rule 4. One re-fetch, one retry, and then the refusal stands.
    if (!result.ok && result.reason === 'auth-failed' && input.online) {
      result = await attempt(true)
    }
  } catch (error) {
    /*
     * The credential could not be obtained: refused by the server, or unreachable. **Nothing was
     * sent**, so nothing is queued — and the caller must say so rather than claim the gate could
     * not be reached, which is a statement about a radio that was never switched on.
     */
    if (!(error instanceof BleAccessRefused) && !(error instanceof NetworkError)) throw error
    return { ok: false, reason: 'no-credential' }
  }

  // Rule 3's exception, checked before the receipt and not after.
  if (!result.ok && result.reason === 'unavailable') return result
  /*
   * 🔸 **A refusal writes no receipt** (R4/D216), and the argument is not `'unavailable'`'s.
   *
   * The gate *was* addressed and it answered, so there is an event here in a way there is not for
   * a radio that never switched on. What there is no room for is a **word**: `failureReason` is
   * the receipt schema's closed vocabulary, so recording this at all would mean either widening a
   * wire enum the server validates — a schema change, not a client fix — or filing it under
   * `timeout`, which is the false sentence this whole change exists to remove. Writing nothing is
   * the honest third option, and it costs little: the refusal's own cause is the command on the
   * **previous** tap, which is already in the trail as a receipt of its own.
   *
   * Vetoable. If the pilot wants refusals counted, the answer is a receipt-vocabulary decision
   * with a server half, not a cast here.
   */
  if (!result.ok && result.reason === 'refused') return result
  if (input.userId === undefined) return result
  // A local `const` rather than `input.userId` at each use: the narrowing above does not survive
  // into the drain's closure, and widening the field to satisfy it would be the wrong repair.
  const userId = input.userId

  /*
   * The handle survives the `try` so the drain below reads **the store this receipt was written
   * to**, and so that `undefined` states the ordering constraint in the type: there is no handle
   * unless a receipt actually landed.
   */
  let queuedTo: KeyValueStore | undefined
  try {
    const store = await input.openStore()
    await queueReceipt(store, input.stage, userId, {
      reconciliationId: newReconciliationId(),
      gateId: input.gateId,
      action: 'open',
      transport: 'ble',
      result: result.ok ? 'success' : 'failure',
      // Both non-receipt reasons (`unavailable`, `refused`) and `no-credential` have returned
      // above, so what is left is `BleFailureReason` — the receipt schema's own vocabulary.
      ...(result.ok ? {} : { failureReason: result.reason as BleFailureReason }),
      occurredAt: new Date().toISOString(),
      ...(result.latencyMs === undefined ? {} : { latencyMs: result.latencyMs }),
    })
    queuedTo = store
  } catch (error) {
    /*
     * **Best-effort, on `rememberProfile`'s rule that a cache is not a gate.** The gate has already
     * moved and the resident has already been told; a quota eviction here costs one Activity row,
     * and letting it reject would replace a true "opened" with a false failure.
     */
    console.warn('receipt-queue-failed', error)
  }

  /*
   * **Rule 5 — a receipt written with reception is uploaded now, not on the next launch.**
   *
   * P16's step 8 had three triggers, all in P6's provider: boot, D128's offline→online edge, and
   * the app bar's refresh. That list was written for the flow P16 describes, whose step 1 begins
   * *"A BLE open completes locally **while the phone is offline**"* — and **D189(e) made BLE a
   * live transport for a phone that is online.** `session.online` starts `true` and only falls on
   * a `NetworkError`, so a phone that never loses reception never produces the edge and boot has
   * already run: a BLE-first open sat in the outbox until the next cold launch. Both screens
   * called `deviceCache.refresh()` here and one of them said in a comment that it drained; it
   * fetched the gate list.
   *
   * This is that fourth trigger, and it is **not** the device-cache refresh the docblock above
   * disclaims: it uploads a row this module just wrote, through P16's own `reconcile.ts`, and
   * touches no P6 state.
   *
   *  - **Never awaited.** The screens render off this function's resolved value, so awaiting would
   *    put a network round trip between the gate moving and the *Sent* chip — PEC-11's *"show
   *    local success immediately"* broken by the very thing that exists to serve the trail.
   *  - **After the queue's `try`, not inside it**, or a drain failure would be logged as
   *    `receipt-queue-failed` — a lie about which half failed. The warn key is the one
   *    `DeviceCacheProvider` already uses for a failure of this same function: keys here name the
   *    failure, not the caller. `drainOutbox` swallows everything its uploader throws and maps a
   *    `NetworkError` to `retry`, so a rejection reaching this `catch` can only be the **store**.
   *  - **`input.online` gates it**, which is `deviceCache.online && reachedNetwork` at both call
   *    sites. A fallback after R4/D217's `never-dispatched` therefore drains nothing — and that is
   *    not a hole: that verdict means a `NetworkError` was already seen, so `session.online` is
   *    `false` and the edge is guaranteed to fire when reception returns. Spending a doomed POST
   *    at the moment the link is worst is what `drainOutbox` declines to do one layer down.
   *
   * 🔸 **A drain already in flight cannot see this receipt, and that is accepted rather than
   * handled.** `reconcileReceipts` is single-flighted and `drainOutbox` takes its `readOutbox`
   * snapshot when it starts, so joining one uploads the rows it found — not ours. The realistic
   * window is two BLE opens a second apart. The row stays queued, durable and un-duplicated, and
   * the next trigger of any kind takes both, oldest opening first. The alternative is a trailing
   * re-run inside a primitive with four callers, to close a window that self-heals.
   */
  if (queuedTo !== undefined && input.online) {
    void (async () => {
      try {
        await reconcileReceipts(queuedTo, input.stage, userId, input.request)
      } catch (error) {
        console.warn('receipt-reconcile-failed', error)
      }
    })()
  }

  return result
}

/**
 * The refusal copy, keyed by *why* rather than by a single "can't open" string — **and shared,
 * because two screens now render it.**
 *
 * `as const satisfies` so each value stays a literal the i18n key type can check: a missing key
 * throws at render, and a map the compiler cannot check is a map that finds out in front of a
 * resident.
 */
export const REFUSAL_KEY = {
  'offline-stale': 'refusalOfflineStale',
  'offline-never-synced': 'refusalOfflineNeverSynced',
  'offline-no-ble': 'refusalOfflineNoBle',
} as const satisfies Record<'offline-stale' | 'offline-never-synced' | 'offline-no-ble', string>

/**
 * **What a local failure the resident is shown can be** (R4/D223).
 *
 * `'refused'` is excluded because a `CMD:Err` is no longer a failure at all: the controller heard
 * the command and answered, so both screens route it to **Success** and neither ever reaches the
 * copy map below. Narrowing the type here rather than only the map is what makes that a compiler
 * rule — a screen that put a refusal into its failure state would stop building rather than
 * render a sentence that no longer exists.
 */
export type RenderedLocalFailure = Exclude<LocalFailureReason, 'refused'>

/** The local-failure copy. `auth-failed` deliberately reuses PEC-25's reconnect-or-service voice. */
export const BLE_FAILURE_KEY = {
  'auth-failed': 'refusalAuthFailed',
  /**
   * **R4 (F7) — `timeout` and `disconnected` shared this string until the split, and that is what
   * made the one reproducible bug on this path undiagnosable.** A second tap while the leaf is
   * travelling yields `localTimeout` on demand; with both reasons rendering the same sentence the
   * screen could not say whether **zero frames came back** on an open session, or whether the
   * **second GATT connection was refused** while the controller was busy. Different mechanisms,
   * different fixes, one sentence. Splitting the key is what lets the device capture (R4 §3.1
   * question 3) read the answer off the screen it is already looking at.
   */
  timeout: 'localTimeout',
  disconnected: 'localDisconnected',
  unavailable: 'localUnavailable',
  /**
   * **The credential never arrived, so the gate was never addressed — and saying otherwise was a
   * false claim, not a vague one.** This case reported `unavailable`, which renders as *"couldn't
   * reach the gate over Bluetooth"*, to a resident whose radio had not been switched on. It is the
   * same rule D134/D135 keep on the other side of the exchange: a message may only assert what
   * something actually witnessed.
   */
  'no-credential': 'localNoCredential',
  /*
   * **`refused` has no entry, and its absence is load-bearing** (R4/D223, consuming D216). D216
   * minted the verdict and gave it `ble.localRefused`; D223 routes it to Success, so the sentence
   * retired from all five catalogs and the key would now be a string nothing can reach. **The
   * verdict itself stays** — without a name of its own a `CMD:Err` would be indistinguishable
   * from a `timeout` again, and routing it to Success would route every silence there with it.
   */
} as const satisfies Record<RenderedLocalFailure, string>

/**
 * **The key an out-of-range sentence actually renders, given the cloud leg that ran before it
 * (D318).**
 *
 * `BLE_FAILURE_KEY` above is unchanged and stays `satisfies Record<RenderedLocalFailure, string>`:
 * this **wraps** it rather than replacing it, because that exhaustiveness is what keeps
 * `'refused'`'s absence load-bearing under D223 — a screen that put a refusal into its failure
 * state must keep failing to compile, and a function that owned the whole mapping would delete
 * that guard.
 *
 * **Why it exists at all.** `decideFallback` dispatches the BLE leg automatically on **both** cloud
 * verdicts in the container (D240, on D189(c)'s reasoning that there is no chooser there to be
 * allowed), so `fallbackOffer` is never set and `ble.fallbackAfterUnconfirmed` — the only place a
 * resident is ever told **a cloud command may already have reached the gate** — renders on the
 * browser PWA alone. An app resident whose cloud leg timed out and whose radio then found nothing
 * read *"Couldn't reach the gate over Bluetooth"*: a clean miss, reported over a command that may
 * well have opened their gate. The verdict is already in hand at both screens, so this is a render
 * branch rather than plumbing.
 *
 * **Only `unavailable` branches.** `timeout`, `disconnected`, `auth-failed` and `no-credential`
 * answer identically on every path, because each already names something that was witnessed — and
 * three sentences rather than two is the whole content of D318 (b): *nothing was sent* and
 * *something may already have arrived* license different next actions, which is the merge
 * D216 → D223 → D294 have each refused.
 *
 * `undefined` is *no cloud leg ran* — a BLE-primary plan, an offline latch, a gate with no cloud
 * path — and keeps {@link BLE_FAILURE_KEY}'s own sentence verbatim (D318 (d)).
 */
export type BleFailureKey =
  | (typeof BLE_FAILURE_KEY)[RenderedLocalFailure]
  | 'localUnavailableAfterOffline'
  | 'localUnavailableAfterUnconfirmed'

export function bleFailureKey(
  reason: RenderedLocalFailure,
  /**
   * **Explicit `undefined` is accepted, on `closeFailureKey`'s rule rather than
   * `deliveryFailurePatch`'s.** This is an argument and not a patch, and `undefined` is the value
   * with the documented meaning — *no cloud leg preceded this* — so forcing a caller to spread a
   * conditional to express it would make the commonest input the hardest one to pass.
   */
  verdict?: CloudVerdict | undefined,
): BleFailureKey {
  if (reason !== 'unavailable') return BLE_FAILURE_KEY[reason]
  if (verdict === 'never-dispatched') return 'localUnavailableAfterOffline'
  if (verdict === 'ambiguous') return 'localUnavailableAfterUnconfirmed'
  return BLE_FAILURE_KEY.unavailable
}
